// src/idb/chunk-db.ts
import Dexie, { Table } from "dexie";
import {
  IChunkRow,
  IUploadRow,
  TChunkStatus,
  TUploadStatus,
} from "../types/db";
import { IUploadMetrics, INetworkQualitySnapshot } from "../types/analytics";
import { Template } from "../../../types/template";
import { Scene, MultiSceneProject } from "../../../types/scene";
import logger from "../utils/logger";

export interface IInterruptedBlankSession {
  recordingId: string;
  layout: any;
  micEnabled: boolean;
  screenSegments: any[];
  webcamSegments: any[];
  audioSegments: any[];
  hasDedicatedAudioStream: boolean;
  mirroredWebcam: boolean;
  avatarWebcam: boolean;
  avatarUrl: string | null;
  edgeToEdge: boolean;
  screenCornerRadius: number;
  quality: string;
  createdAt: number;
  lastUpdatedAt: number;
  status: 'recording' | 'interrupted' | 'recovering';
}

export interface IBlankSessionChunk {
  id: string;
  recordingId: string;
  streamType: 'screen' | 'webcam' | 'audio';
  segmentIndex: number;
  chunkIndex: number;
  blob: Blob;
  createdAt: number;
}

export interface IPendingRecordingRow {
  id: string;
  sceneId: string;
  projectId?: string;
  screenBlob?: Blob;
  webcamBlob?: Blob;
  screenSegments: Array<{
    segmentIndex: number;
    blob: Blob;
    tsStart: number;
    tsEnd: number | null;
    hasAudio: boolean;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  webcamSegments: Array<{
    segmentIndex: number;
    blob: Blob;
    tsStart: number;
    tsEnd: number | null;
    hasAudio: boolean;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  // Dedicated audio stream segments (separate from video audio tracks)
  audioSegments?: Array<{
    segmentIndex: number;
    blob: Blob;
    tsStart: number;
    tsEnd: number | null;
    hasMic: boolean;
    hasSystemAudio: boolean;
    isMuted: boolean;
  }>;
  layout: any;
  duration: number;
  micEnabled: boolean;
  createdAt: number;
}

class RecorderDB extends Dexie {
  chunks!: Table<IChunkRow, string>;
  uploads!: Table<IUploadRow, string>;
  analytics!: Table<IUploadMetrics, string>;          // Sprint 9: Upload metrics
  networkQuality!: Table<INetworkQualitySnapshot, string>; // Sprint 9: Network quality
  templates!: Table<Template, string>;                // Phase 2 Sprint 13: Templates
  scenes!: Table<Scene, string>;                      // Phase 2 Sprint 15: Scenes
  projects!: Table<MultiSceneProject, string>;        // Phase 2 Sprint 15: Multi-scene projects
  customTemplates!: Table<Template, string>;          // Phase 2 Sprint 19: Custom user templates
  pendingRecordings!: Table<IPendingRecordingRow, string>; // Pending scene recordings before upload
  interruptedBlankSessions!: Table<IInterruptedBlankSession, string>; // Blank mode crash recovery sessions
  blankSessionChunks!: Table<IBlankSessionChunk, string>; // Blank mode crash recovery chunks
  
  constructor() {
    super("DadanRecorderDB");
    this.version(4).stores({
      // indexes for fast lookups/progress
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
    });
    
    // Sprint 9: Add analytics and network quality tables
    this.version(5).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
      analytics: "id, videoGuid, timestamp",
      networkQuality: "id, timestamp",
    });
    
    // Phase 2 Sprint 13: Add templates table
    this.version(6).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
      analytics: "id, videoGuid, timestamp",
      networkQuality: "id, timestamp",
      templates: "id, category, isOfficial, createdAt",
    });
    
    // Phase 2 Sprint 15-16: Add scenes and projects tables for multi-scene recording
    this.version(7).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
      analytics: "id, videoGuid, timestamp",
      networkQuality: "id, timestamp",
      templates: "id, category, isOfficial, createdAt",
      scenes: "id, order, status, recordedAt",
      projects: "id, status, templateId, createdAt, updatedAt",
    });
    
    // Phase 2 Sprint 19: Add custom templates table for user-created templates
    this.version(8).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
      analytics: "id, videoGuid, timestamp",
      networkQuality: "id, timestamp",
      templates: "id, category, isOfficial, createdAt",
      scenes: "id, order, status, recordedAt",
      projects: "id, status, templateId, createdAt, updatedAt",
      customTemplates: "id, category, createdBy, createdAt, updatedAt",
    });
    
    // Version 9: Add projectId index to scenes table for pending upload queries
    this.version(9).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
      analytics: "id, videoGuid, timestamp",
      networkQuality: "id, timestamp",
      templates: "id, category, isOfficial, createdAt",
      scenes: "id, projectId, order, status, recordedAt, [projectId+status]",
      projects: "id, status, templateId, createdAt, updatedAt",
      customTemplates: "id, category, createdBy, createdAt, updatedAt",
    });
    
    // Version 10: Add pendingRecordings table for storing scene recordings before upload confirmation
    this.version(10).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
      analytics: "id, videoGuid, timestamp",
      networkQuality: "id, timestamp",
      templates: "id, category, isOfficial, createdAt",
      scenes: "id, projectId, order, status, recordedAt, [projectId+status]",
      projects: "id, status, templateId, createdAt, updatedAt",
      customTemplates: "id, category, createdBy, createdAt, updatedAt",
      pendingRecordings: "id, sceneId, projectId, createdAt",
    });
    
    // Version 11: Add blank mode crash recovery tables
    this.version(11).stores({
      chunks:
        "id, videoGuid, status, chunkNo, updatedAt, [videoGuid+chunkNo], [videoGuid+status]",
      uploads: "videoGuid, status, createdAt, updatedAt",
      analytics: "id, videoGuid, timestamp",
      networkQuality: "id, timestamp",
      templates: "id, category, isOfficial, createdAt",
      scenes: "id, projectId, order, status, recordedAt, [projectId+status]",
      projects: "id, status, templateId, createdAt, updatedAt",
      customTemplates: "id, category, createdBy, createdAt, updatedAt",
      pendingRecordings: "id, sceneId, projectId, createdAt",
      interruptedBlankSessions: "recordingId, status, createdAt",
      blankSessionChunks: "id, recordingId, streamType, segmentIndex, [recordingId+streamType+segmentIndex]",
    });
  }
}

export const db = new RecorderDB();

// convenience helpers
export async function putChunk(row: IChunkRow) {
  await db.chunks.put(row);
}

export async function markLastChunk(videoGuid: string, chunkNo: number) {
  const id = `${videoGuid}:${chunkNo}`;
  const row = await db.chunks.get(id);
  if (!row) return;
  row.isLast = true;
  row.updatedAt = Date.now();
  await db.chunks.put(row);
}

export async function updateChunkStatus(
  videoGuid: string,
  chunkNo: number,
  status: TChunkStatus
) {
  const id = `${videoGuid}:${chunkNo}`;
  const row = await db.chunks.get(id);
  if (!row) return;
  row.status = status;
  row.updatedAt = Date.now();
  await db.chunks.put(row);
}

// Remove all chunks for a given videoGuid
export async function deleteChunks(videoGuid: string) {
  await db.chunks.where("videoGuid").equals(videoGuid).delete();
}

// Update a specific chunk with uploadId and key for abort capability
export async function updateChunkWithUploadInfo(
  videoGuid: string,
  chunkNo: number,
  uploadId: string,
  key: string
) {
  await db.chunks
    .where("id")
    .equals(`${videoGuid}:${chunkNo}`)
    .modify({ uploadId, key, updatedAt: Date.now() });
}

export async function deleteChunkById(id: string) {
  await db.chunks.delete(id);
}

// Upload management functions
export async function putUpload(row: IUploadRow) {
  await db.uploads.put(row);
}

export async function getUpload(
  videoGuid: string
): Promise<IUploadRow | undefined> {
  return await db.uploads.get(videoGuid);
}

export async function updateUploadStatus(
  videoGuid: string,
  status: TUploadStatus
) {
  const row = await db.uploads.get(videoGuid);
  if (!row) return;
  row.status = status;
  row.updatedAt = Date.now();
  await db.uploads.put(row);
}

export async function updateUploadProgress(
  videoGuid: string,
  uploadedChunks: number,
  parts: Array<{ ETag: string; PartNumber: number; "content-length": string }>
) {
  const row = await db.uploads.get(videoGuid);
  if (!row) return;
  row.uploadedChunks = uploadedChunks;
  row.parts = parts;
  row.updatedAt = Date.now();
  await db.uploads.put(row);
}

export async function deleteUpload(videoGuid: string) {
  await db.uploads.delete(videoGuid);
}

export async function getAllActiveUploads(): Promise<IUploadRow[]> {
  return await db.uploads
    .where("status")
    .anyOf(["initializing", "active"])
    .toArray();
}

export async function getUploadsByStatus(
  status: TUploadStatus
): Promise<IUploadRow[]> {
  return await db.uploads.where("status").equals(status).toArray();
}

export async function canResumeUpload(videoGuid: string): Promise<boolean> {
  const upload = await getUpload(videoGuid);
  return !!(
    upload &&
    upload.videoId &&
    upload.uploadId &&
    upload.key &&
    upload.status !== "completed"
  );
}

export async function canResumeFromVideoId(
  videoGuid: string
): Promise<boolean> {
  const upload = await getUpload(videoGuid);

  return !!(
    upload &&
    upload.videoId &&
    !upload.uploadId &&
    !upload.key &&
    upload.status !== "completed"
  );
}

// Check if any chunk statuses are error or pending for a given videoGuid
export async function shouldShowRetryButton(
  videoGuid: string
): Promise<boolean> {
  try {
    const chunks = await db.chunks
      .where("videoGuid")
      .equals(videoGuid)
      .toArray();

    if (chunks.length === 0) {
      return false;
    }

    const errorOrPendingChunks = chunks.filter(
      (chunk) =>
        chunk.status === "error" ||
        chunk.status === "pending" ||
        chunk.status === "uploading"
      // chunk.status === "done"
    );

    // Show retry button if ANY chunks are error or pending
    // This ensures users can retry even if only 1 out of many chunks fails
    return errorOrPendingChunks.length > 0;
  } catch (error) {
    console.error("Error checking chunk statuses:", error);
    return false;
  }
}

// Detect stuck chunks on page load (uploading, pending, error states)
export async function getStuckChunks(): Promise<{
  videoGuids: string[];
  totalChunks: number;
  stuckChunks: Array<{
    videoGuid: string;
    uploadId?: string;
    key?: string;
    chunks: Array<{
      chunkNo: number;
      status: TChunkStatus;
      size: number;
      createdAt: number;
    }>;
  }>;
}> {
  try {
    // Get all chunks that are not in 'done' status
    const stuckChunks = await db.chunks
      .where("status")
      .anyOf(["uploading", "pending", "error"]) // "done"
      .toArray();

    if (stuckChunks.length === 0) {
      return { videoGuids: [], totalChunks: 0, stuckChunks: [] };
    }

    // Group by videoGuid
    const groupedByVideoGuid = stuckChunks.reduce((acc, chunk) => {
      if (!acc[chunk.videoGuid]) {
        acc[chunk.videoGuid] = {
          uploadId: chunk.uploadId,
          key: chunk.key,
          chunks: [],
        };
      }
      acc[chunk.videoGuid].chunks.push({
        chunkNo: chunk.chunkNo,
        status: chunk.status,
        size: chunk.size,
        createdAt: chunk.createdAt,
      });
      return acc;
    }, {} as Record<string, { uploadId?: string; key?: string; chunks: Array<{ chunkNo: number; status: TChunkStatus; size: number; createdAt: number }> }>);

    const videoGuids = Object.keys(groupedByVideoGuid);
    const stuckChunksArray = videoGuids.map((videoGuid) => ({
      videoGuid,
      uploadId: groupedByVideoGuid[videoGuid].uploadId,
      key: groupedByVideoGuid[videoGuid].key,
      chunks: groupedByVideoGuid[videoGuid].chunks.sort(
        (a: { chunkNo: number }, b: { chunkNo: number }) =>
          a.chunkNo - b.chunkNo
      ),
    }));

    return {
      videoGuids,
      totalChunks: stuckChunks.length,
      stuckChunks: stuckChunksArray,
    };
  } catch (error) {
    console.error("Error detecting stuck chunks:", error);
    return { videoGuids: [], totalChunks: 0, stuckChunks: [] };
  }
}

// Helper function to check if all chunks are marked as done in database
export async function areAllChunksDone(videoGuid: string): Promise<boolean> {
  try {
    const chunks = await db.chunks
      .where("videoGuid")
      .equals(videoGuid)
      .toArray();

    if (chunks.length === 0) {
      return false;
    }

    // Check if all chunks have status "done"
    const allDone = chunks.every((chunk) => chunk.status === "done");

    logger.info(`Chunk status check for ${videoGuid}:`, {
      totalChunks: chunks.length,
      doneChunks: chunks.filter((c) => c.status === "done").length,
      allDone,
      chunkStatuses: chunks.map((c) => ({
        chunkNo: c.chunkNo,
        status: c.status,
      })),
    });

    return allDone;
  } catch (error) {
    console.error(`Error checking chunk status for ${videoGuid}:`, error);
    return false;
  }
}

/**
 * Sprint 8: Get chunks that need to be uploaded (partial resume support)
 * Returns chunks with status: pending, error, or uploading
 */
export async function getChunksToUpload(videoGuid: string): Promise<{
  totalChunks: number;
  completedChunks: number;
  chunksToUpload: IChunkRow[];
  resumeFromChunk: number | null;
}> {
  try {
    const allChunks = await db.chunks
      .where("videoGuid")
      .equals(videoGuid)
      .toArray();

    if (allChunks.length === 0) {
      return {
        totalChunks: 0,
        completedChunks: 0,
        chunksToUpload: [],
        resumeFromChunk: null,
      };
    }

    // Sort by chunkNo for ordered processing
    allChunks.sort((a, b) => a.chunkNo - b.chunkNo);

    const completedChunks = allChunks.filter((c) => c.status === "done");
    const chunksToUpload = allChunks.filter(
      (c) => c.status === "pending" || c.status === "error" || c.status === "uploading"
    );

    // Find the first chunk that needs upload (resume point)
    const resumeFromChunk = chunksToUpload.length > 0 
      ? chunksToUpload[0].chunkNo 
      : null;

    logger.info(`Partial upload resume for ${videoGuid}:`, {
      totalChunks: allChunks.length,
      completedChunks: completedChunks.length,
      chunksToUpload: chunksToUpload.length,
      resumeFromChunk,
      statusBreakdown: {
        done: completedChunks.length,
        pending: allChunks.filter((c) => c.status === "pending").length,
        error: allChunks.filter((c) => c.status === "error").length,
        uploading: allChunks.filter((c) => c.status === "uploading").length,
      },
    });

    return {
      totalChunks: allChunks.length,
      completedChunks: completedChunks.length,
      chunksToUpload,
      resumeFromChunk,
    };
  } catch (error) {
    console.error(`Error getting chunks to upload for ${videoGuid}:`, error);
    return {
      totalChunks: 0,
      completedChunks: 0,
      chunksToUpload: [],
      resumeFromChunk: null,
    };
  }
}

// ============================================================================
// Sprint 9: Analytics Functions
// ============================================================================

/**
 * Store upload metrics after upload completes
 */
export async function putUploadMetrics(metrics: IUploadMetrics): Promise<void> {
  try {
    await db.analytics.put(metrics);
    logger.info(`Stored upload metrics for ${metrics.videoGuid}:`, {
      avgSpeed: metrics.avgSpeed,
      successRate: metrics.chunkSuccessRate,
    });
    
    // Rotate if exceeds 100 entries
    await rotateAnalytics();
  } catch (error) {
    console.error('Error storing upload metrics:', error);
  }
}

/**
 * Get all upload metrics
 */
export async function getAllUploadMetrics(): Promise<IUploadMetrics[]> {
  return await db.analytics.orderBy('timestamp').reverse().toArray();
}

/**
 * Get upload metrics for a specific video
 */
export async function getUploadMetricsByVideo(videoGuid: string): Promise<IUploadMetrics | undefined> {
  return await db.analytics.where('videoGuid').equals(videoGuid).first();
}

/**
 * Get upload metrics within date range
 */
export async function getUploadMetricsByDateRange(
  startTimestamp: number,
  endTimestamp: number
): Promise<IUploadMetrics[]> {
  return await db.analytics
    .where('timestamp')
    .between(startTimestamp, endTimestamp, true, true)
    .toArray();
}

/**
 * Delete old analytics (keep max 100 entries, max 30 days)
 */
async function rotateAnalytics(): Promise<void> {
  try {
    const allMetrics = await db.analytics.orderBy('timestamp').reverse().toArray();
    
    // Delete entries older than 30 days
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const oldEntries = allMetrics.filter(m => m.timestamp < thirtyDaysAgo);
    
    if (oldEntries.length > 0) {
      await db.analytics.bulkDelete(oldEntries.map(m => m.id));
      logger.info(`Deleted ${oldEntries.length} old analytics entries (>30 days)`);
    }
    
    // Keep only most recent 100 entries
    if (allMetrics.length > 100) {
      const entriesToDelete = allMetrics.slice(100);
      await db.analytics.bulkDelete(entriesToDelete.map(m => m.id));
      logger.info(`Rotated analytics: deleted ${entriesToDelete.length} oldest entries`);
    }
  } catch (error) {
    console.error('Error rotating analytics:', error);
  }
}

/**
 * Store network quality snapshot
 */
export async function putNetworkQualitySnapshot(snapshot: INetworkQualitySnapshot): Promise<void> {
  try {
    await db.networkQuality.put(snapshot);
    
    // Rotate if exceeds 288 entries (48 per day × 6 days)
    await rotateNetworkQuality();
  } catch (error) {
    console.error('Error storing network quality snapshot:', error);
  }
}

/**
 * Get recent network quality snapshots
 */
export async function getRecentNetworkQuality(limit: number = 48): Promise<INetworkQualitySnapshot[]> {
  return await db.networkQuality
    .orderBy('timestamp')
    .reverse()
    .limit(limit)
    .toArray();
}

/**
 * Get network quality for date range
 */
export async function getNetworkQualityByDateRange(
  startTimestamp: number,
  endTimestamp: number
): Promise<INetworkQualitySnapshot[]> {
  return await db.networkQuality
    .where('timestamp')
    .between(startTimestamp, endTimestamp, true, true)
    .toArray();
}

/**
 * Delete old network quality snapshots (keep max 288 entries, 6 days)
 */
async function rotateNetworkQuality(): Promise<void> {
  try {
    const allSnapshots = await db.networkQuality.orderBy('timestamp').reverse().toArray();
    
    // Delete entries older than 6 days
    const sixDaysAgo = Date.now() - (6 * 24 * 60 * 60 * 1000);
    const oldEntries = allSnapshots.filter(s => s.timestamp < sixDaysAgo);
    
    if (oldEntries.length > 0) {
      await db.networkQuality.bulkDelete(oldEntries.map(s => s.id));
      logger.info(`Deleted ${oldEntries.length} old network snapshots (>6 days)`);
    }
    
    // Keep only most recent 288 entries
    if (allSnapshots.length > 288) {
      const entriesToDelete = allSnapshots.slice(288);
      await db.networkQuality.bulkDelete(entriesToDelete.map(s => s.id));
      logger.info(`Rotated network quality: deleted ${entriesToDelete.length} oldest entries`);
    }
  } catch (error) {
    console.error('Error rotating network quality:', error);
  }
}

/**
 * Clear all analytics data
 */
export async function clearAllAnalytics(): Promise<void> {
  try {
    await db.analytics.clear();
    await db.networkQuality.clear();
    logger.info('Cleared all analytics data');
  } catch (error) {
    console.error('Error clearing analytics:', error);
  }
}

// ========== Pending Recordings (Scene mode deferred upload) ==========

/**
 * Save a pending recording for later upload
 */
export async function savePendingRecording(row: IPendingRecordingRow): Promise<void> {
  try {
    await db.pendingRecordings.put(row);
    logger.info(`[PendingRecording] Saved pending recording ${row.id} for scene ${row.sceneId}`);
  } catch (error) {
    console.error('[PendingRecording] Error saving pending recording:', error);
    throw error;
  }
}

/**
 * Get a pending recording by scene ID (returns the most recent one)
 */
export async function getPendingRecordingBySceneId(sceneId: string): Promise<IPendingRecordingRow | undefined> {
  try {
    // Get all pending recordings for this sceneId and return the most recent one
    const allPending = await db.pendingRecordings.where('sceneId').equals(sceneId).toArray();
    if (allPending.length === 0) return undefined;
    // Sort by createdAt descending and return the newest
    return allPending.sort((a, b) => b.createdAt - a.createdAt)[0];
  } catch (error) {
    console.error('[PendingRecording] Error getting pending recording:', error);
    return undefined;
  }
}

/**
 * Delete ALL pending recordings for a scene ID (cleanup stale data)
 */
export async function deleteAllPendingRecordingsForScene(sceneId: string): Promise<number> {
  try {
    const count = await db.pendingRecordings.where('sceneId').equals(sceneId).delete();
    if (count > 0) {
      logger.info(`[PendingRecording] Deleted ${count} pending recording(s) for scene ${sceneId}`);
    }
    return count;
  } catch (error) {
    console.error('[PendingRecording] Error deleting pending recordings for scene:', error);
    return 0;
  }
}

/**
 * Get all pending recordings for a project
 */
export async function getPendingRecordingsForProject(projectId: string): Promise<IPendingRecordingRow[]> {
  try {
    return await db.pendingRecordings.where('projectId').equals(projectId).toArray();
  } catch (error) {
    console.error('[PendingRecording] Error getting pending recordings for project:', error);
    return [];
  }
}

/**
 * Get all pending recordings
 */
export async function getAllPendingRecordings(): Promise<IPendingRecordingRow[]> {
  try {
    return await db.pendingRecordings.toArray();
  } catch (error) {
    console.error('[PendingRecording] Error getting all pending recordings:', error);
    return [];
  }
}

/**
 * Delete a pending recording after successful upload
 */
export async function deletePendingRecording(id: string): Promise<void> {
  try {
    await db.pendingRecordings.delete(id);
    logger.info(`[PendingRecording] Deleted pending recording ${id}`);
  } catch (error) {
    console.error('[PendingRecording] Error deleting pending recording:', error);
    throw error;
  }
}

/**
 * Clear all pending recordings for a project (e.g., when project is deleted)
 */
export async function clearPendingRecordingsForProject(projectId: string): Promise<void> {
  try {
    await db.pendingRecordings.where('projectId').equals(projectId).delete();
    logger.info(`[PendingRecording] Cleared all pending recordings for project ${projectId}`);
  } catch (error) {
    console.error('[PendingRecording] Error clearing pending recordings:', error);
  }
}

/**
 * Clean up old pending recordings (older than 24 hours)
 */
export async function cleanupOldPendingRecordings(): Promise<void> {
  try {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const oldRecordings = await db.pendingRecordings.where('createdAt').below(oneDayAgo).toArray();
    
    if (oldRecordings.length > 0) {
      await db.pendingRecordings.bulkDelete(oldRecordings.map(r => r.id));
      logger.info(`[PendingRecording] Cleaned up ${oldRecordings.length} old pending recordings`);
    }
  } catch (error) {
    console.error('[PendingRecording] Error cleaning up old pending recordings:', error);
  }
}

// ========== Blank Mode Crash Recovery ==========

export async function saveBlankSession(session: IInterruptedBlankSession): Promise<void> {
  try {
    await db.interruptedBlankSessions.put(session);
    logger.info(`[BlankSession] Saved session ${session.recordingId}`);
  } catch (error) {
    logger.warn('[BlankSession] Error saving session:', error);
    throw error;
  }
}

export async function updateBlankSessionSegments(
  recordingId: string,
  screenSegments: any[],
  webcamSegments: any[],
  audioSegments: any[]
): Promise<void> {
  try {
    await db.interruptedBlankSessions.update(recordingId, {
      screenSegments,
      webcamSegments,
      audioSegments,
      lastUpdatedAt: Date.now(),
    });
  } catch (error) {
    logger.warn('[BlankSession] Error updating segments:', error);
    throw error;
  }
}

export async function getInterruptedBlankSessions(): Promise<IInterruptedBlankSession[]> {
  try {
    return await db.interruptedBlankSessions
      .where('status')
      .anyOf(['recording', 'interrupted'])
      .toArray();
  } catch (error) {
    logger.warn('[BlankSession] Error getting interrupted sessions:', error);
    return [];
  }
}

export async function deleteBlankSession(recordingId: string): Promise<void> {
  try {
    await db.interruptedBlankSessions.delete(recordingId);
    await db.blankSessionChunks.where('recordingId').equals(recordingId).delete();
    logger.info(`[BlankSession] Deleted session and chunks for ${recordingId}`);
  } catch (error) {
    logger.warn('[BlankSession] Error deleting session:', error);
    throw error;
  }
}

export async function saveBlankSessionChunks(chunks: IBlankSessionChunk[]): Promise<void> {
  try {
    await db.blankSessionChunks.bulkPut(chunks);
  } catch (error) {
    logger.warn('[BlankSession] Error saving chunks:', error);
    throw error;
  }
}

export async function getBlankSessionChunks(
  recordingId: string,
  streamType: string,
  segmentIndex: number
): Promise<IBlankSessionChunk[]> {
  try {
    return await db.blankSessionChunks
      .where('[recordingId+streamType+segmentIndex]')
      .equals([recordingId, streamType, segmentIndex])
      .toArray();
  } catch (error) {
    logger.warn('[BlankSession] Error getting chunks:', error);
    return [];
  }
}

export async function getAllBlankSessionChunks(recordingId: string): Promise<IBlankSessionChunk[]> {
  try {
    return await db.blankSessionChunks.where('recordingId').equals(recordingId).toArray();
  } catch (error) {
    logger.warn('[BlankSession] Error getting all chunks:', error);
    return [];
  }
}

export async function markBlankSessionInterrupted(recordingId: string): Promise<void> {
  try {
    await db.interruptedBlankSessions.update(recordingId, {
      status: 'interrupted',
      lastUpdatedAt: Date.now(),
    });
  } catch (error) {
    logger.warn('[BlankSession] Error marking interrupted:', error);
  }
}

export async function markBlankSessionRecovering(recordingId: string): Promise<void> {
  try {
    await db.interruptedBlankSessions.update(recordingId, {
      status: 'recovering',
      lastUpdatedAt: Date.now(),
    });
  } catch (error) {
    logger.warn('[BlankSession] Error marking recovering:', error);
  }
}
